import {
  ServiceProviderAssessmentTemplateQuestionType,
  ServiceProviderAssessmentTemplateStatus,
} from '@/enum/global.enum'
import { nameField } from '@/schema/common/name-field.schema'
import { z } from 'zod'

export const addVendorUsersSchema = z.object({
  serviceProviderId: z.string().min(1, 'Supplier is required'),
  vendorUsers: z
    .array(
      z.object({
        email: z.string().min(1, 'Email is required').email('Enter a valid email'),
        firstName: z.string().max(100, 'First name is too long').optional(),
        lastName: z.string().max(100, 'Last name is too long').optional(),
      })
    )
    .min(1, 'Add at least one vendor user'),
})

export type AddVendorUsersSchemaType = z.infer<typeof addVendorUsersSchema>

export const vendorRegistrationSchema = z
  .object({
    email: z.string().min(1, 'Email is required').email('Enter a valid email'),
    firstName: z.string().min(1, 'First name is required').max(100, 'First name is too long'),
    lastName: z.string().min(1, 'Last name is required').max(100, 'Last name is too long'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export type VendorRegistrationSchemaType = z.infer<typeof vendorRegistrationSchema>

export const inviteCollaboratorSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  firstName: z.string().max(100, 'First name is too long').optional(),
  lastName: z.string().max(100, 'Last name is too long').optional(),
  accessExpiresAt: z.string().optional(),
})

export type InviteCollaboratorSchemaType = z.infer<typeof inviteCollaboratorSchema>

export const createServiceTypeSchema = z.object({
  name: nameField('Service Type', 1, 100),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  active: z.boolean().optional(),
  autoSave: z.boolean().optional(),
})

export const updateServiceTypeSchema = createServiceTypeSchema.extend({
  id: z.string().min(1, 'ID is required'),
})

export type CreateServiceTypeSchemaType = z.infer<typeof createServiceTypeSchema>
export type UpdateServiceTypeSchemaType = z.infer<typeof updateServiceTypeSchema>

export const createAssessmentTierSchema = z.object({
  name: nameField('Assessment Tier', 1, 100),
  color: z.string().min(1, 'Assessment Tier Color is required'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  active: z.boolean().optional(),
  autoSave: z.boolean().optional(),
})

export const updateAssessmentTierSchema = createAssessmentTierSchema.extend({
  id: z.string().min(1, 'ID is required'),
})

export type CreateAssessmentTierSchemaType = z.infer<typeof createAssessmentTierSchema>
export type UpdateAssessmentTierSchemaType = z.infer<typeof updateAssessmentTierSchema>

const sequenceField = z
  .number()
  .int('Sequence must be a whole number')
  .positive('Sequence must be greater than 0')
  .optional()

export const createAssessmentTierCriticalitySchema = z.object({
  name: nameField('Criticality', 1, 100),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  sequence: sequenceField,
  active: z.boolean(),
})

export const updateAssessmentTierCriticalitySchema = createAssessmentTierCriticalitySchema.extend({
  id: z.string().min(1, 'ID is required'),
})

export type CreateAssessmentTierCriticalitySchemaType = z.infer<typeof createAssessmentTierCriticalitySchema>
export type UpdateAssessmentTierCriticalitySchemaType = z.infer<typeof updateAssessmentTierCriticalitySchema>

export const createAssessmentTierSensitivitySchema = z.object({
  name: nameField('Sensitivity', 1, 100),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  sequence: sequenceField,
  active: z.boolean(),
})

export const updateAssessmentTierSensitivitySchema = createAssessmentTierSensitivitySchema.extend({
  id: z.string().min(1, 'ID is required'),
})

export type CreateAssessmentTierSensitivitySchemaType = z.infer<typeof createAssessmentTierSensitivitySchema>
export type UpdateAssessmentTierSensitivitySchemaType = z.infer<typeof updateAssessmentTierSensitivitySchema>

export const createServiceProviderContractStatusSchema = z.object({
  name: nameField('Supply Chain Contract Status', 1, 100),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  active: z.boolean().optional(),
  autoSave: z.boolean().optional(),
})

export const updateServiceProviderContractStatusSchema = createServiceProviderContractStatusSchema.extend({
  id: z.string().min(1, 'ID is required'),
})

export type CreateServiceProviderContractStatusSchemaType = z.infer<typeof createServiceProviderContractStatusSchema>
export type UpdateServiceProviderContractStatusSchemaType = z.infer<typeof updateServiceProviderContractStatusSchema>

export const createServiceProviderGeneralDetailsSchema = z.object({
  name: nameField('Name', 1, 200),
  // Optional: a supplier may have no contact email. Format is still checked when one is entered.
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  serviceTypeId: z.string().min(1, 'Please select a service type'),
  description: z.string().max(50000, 'Description must be less than 50000 characters').optional(),
  pointOfContact: z
    .string()
    .min(1, 'Point of Contact is required')
    .max(100, 'Point of Contact must be less than 100 characters'),
  autoSave: z.boolean().optional(),
  sourceFileIds: z.array(z.string()).optional(),
  deletedFileIds: z.array(z.string()).optional(),
  informationAssetIds: z.array(z.string()).optional(),
  policyIds: z.array(z.string()).optional(),
  country: z.string().optional(),
})

export const updateServiceProviderGeneralDetailsSchema = createServiceProviderGeneralDetailsSchema.extend({
  id: z.string().min(1, 'ID is required'),
})

export const createServiceProviderAssuranceProfileDetailsSchema = z
  .object({
    id: z.string().min(1, 'ID is required'),
    contractStatusId: z.string().min(1, 'Please select a contract status'),
    contractStartDate: z.string().optional(),
    contractEndDate: z.string().optional(),
    assessmentTierCriticalityId: z.string().min(1, 'Please select a criticality'),
    assessmentTierSensitivityId: z.string().min(1, 'Please select a sensitivity'),
    riskRatingId: z.string().optional(),
    dataShared: z.string().optional(),
    lastReviewDate: z.string().optional(),
    nextReviewDate: z.string().optional(),
    autoSave: z.boolean().optional(),
    // Required at the form level only when the selected criticality differs from the
    // active SIA recommendation (the recommendation is runtime-only, so it is enforced
    // in the component via setError rather than statically here).
    criticalityOverrideJustification: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.lastReviewDate && data.nextReviewDate)
        return new Date(data.nextReviewDate) > new Date(data.lastReviewDate)
      return true
    },
    {
      message: 'Next review date must be greater than last review date',
      path: ['nextReviewDate'],
    }
  )
  .refine(
    (data) => {
      if (data.contractStartDate && data.contractEndDate) {
        return new Date(data.contractEndDate) > new Date(data.contractStartDate)
      }
      return true
    },
    {
      message: 'End date must be greater than start date',
      path: ['contractEndDate'],
    }
  )

export type CreateServiceProviderGeneralDetailsSchemaType = z.infer<typeof createServiceProviderGeneralDetailsSchema>
export type UpdateServiceProviderGeneralDetailsSchemaType = z.infer<typeof updateServiceProviderGeneralDetailsSchema>

export const createServiceProviderDescriptionAIPromptSchema = z.object({
  aiPrompt: z
    .string()
    .trim()
    .min(20, 'AI Prompt must be at least 20 characters')
    .max(5000, 'AI Prompt must be less than 5000 characters'),
})

export type CreateServiceProviderDescriptionAIPromptSchemaType = z.infer<
  typeof createServiceProviderDescriptionAIPromptSchema
>

export type CreateServiceProviderAssuranceProfileDetailsSchemaType = z.infer<
  typeof createServiceProviderAssuranceProfileDetailsSchema
>

export const createServiceProviderAssessmentTemplateSchema = z.object({
  name: nameField('Name', 1, 200),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  active: z.boolean().optional(),
  status: z.nativeEnum(ServiceProviderAssessmentTemplateStatus, { message: 'Please select a status' }),
  autoSave: z.boolean().optional(),
})

export const updateServiceProviderAssessmentTemplateSchema = createServiceProviderAssessmentTemplateSchema.extend({
  id: z.string().min(1, 'ID is required'),
})

export type CreateServiceProviderAssessmentTemplateSchemaType = z.infer<
  typeof createServiceProviderAssessmentTemplateSchema
>
export type UpdateServiceProviderAssessmentTemplateSchemaType = z.infer<
  typeof updateServiceProviderAssessmentTemplateSchema
>

export const createServiceProviderAssessmentTemplateQuestionnaireSchema = z.object({
  assessmentTemplateId: z.string().min(1, 'Assessment Template is required'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  sequence: z.number().optional(),
  active: z.boolean().optional(),
})

export const updateServiceProviderAssessmentTemplateQuestionnaireSchema =
  createServiceProviderAssessmentTemplateQuestionnaireSchema.extend({
    id: z.string().min(1, 'ID is required'),
  })

export type CreateServiceProviderAssessmentTemplateQuestionnaireSchemaType = z.infer<
  typeof createServiceProviderAssessmentTemplateQuestionnaireSchema
>
export type UpdateServiceProviderAssessmentTemplateQuestionnaireSchemaType = z.infer<
  typeof updateServiceProviderAssessmentTemplateQuestionnaireSchema
>

export const createServiceProviderAssessmentTemplateQuestionnaireQuestionSchema = z.object({
  questionnaireId: z.string().min(1, 'Questionnaire is required'),
  question: z.string().min(1, 'Question is required'),
  type: z.nativeEnum(ServiceProviderAssessmentTemplateQuestionType, { message: 'Please select a type' }),
  sequence: z.number().optional(),
  options: z.string().optional(),
  placeholder: z.string().optional(),
  expectedAnswer: z.string().optional(),
  required: z.boolean().optional(),
  active: z.boolean().optional(),
  conditionalLogic: z.string().optional(),
  maxFileSize: z.number().optional(),
  allowedFileTypes: z.string().optional(),
})

export const updateServiceProviderAssessmentTemplateQuestionnaireQuestionSchema =
  createServiceProviderAssessmentTemplateQuestionnaireQuestionSchema.extend({
    id: z.string().min(1, 'ID is required'),
  })

export type CreateServiceProviderAssessmentTemplateQuestionnaireQuestionSchemaType = z.infer<
  typeof createServiceProviderAssessmentTemplateQuestionnaireQuestionSchema
>
export type UpdateServiceProviderAssessmentTemplateQuestionnaireQuestionSchemaType = z.infer<
  typeof updateServiceProviderAssessmentTemplateQuestionnaireQuestionSchema
>

// Minimal quick-create used by inline "Add New Supply Chain Management" selects (e.g. CTRA questionnaire recipients)
export const createServiceProviderQuickSchema = z.object({
  name: nameField('Name', 1, 200),
  email: z.string().email('Enter a valid email').optional().or(z.literal('')),
  serviceTypeId: z.string().optional(),
})

export type CreateServiceProviderQuickSchemaType = z.infer<typeof createServiceProviderQuickSchema>
